import { PRICEME_AD_PLACEMENT_ATTR, PRICEME_AD_TYPE_ATTR } from "@/ads/priceme-ad-placement";

/**
 * Single source of truth for fullscreen sticky-footer height (Raptive/Fancybox). Keep in sync with App.scss `--priceme-fullscreen-sticky-ad-height`.
 */
export const STICKY_FOOTER_AD_RESERVED_HEIGHT_PX = 90 as const;

export const FULLSCREEN_STICKY_FOOTER_BODY_CLASS = "priceme-fullscreen-viewer-open";

export const FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX = 8;

/** Ad slot + inner bottom padding below the anchor (excludes safe-area inset). */
export const STICKY_FOOTER_LAYOUT_RESERVE_PX =
    STICKY_FOOTER_AD_RESERVED_HEIGHT_PX + FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX;

export const FULLSCREEN_STICKY_FOOTER_DESKTOP_MIN_WIDTH_PX = 768;

export const FULLSCREEN_FANCYBOX_FOOTER_AD_ELEMENT_ID = "priceme-fancybox-footer-ad";

export const FULLSCREEN_FOOTER_PORTAL_ROOT_ELEMENT_ID = "priceme-fullscreen-footer-ad-portal";

/** If image uses more than this fraction of viewport height, hide sticky ad on mobile to reduce overlap risk. */
export const FULLSCREEN_STICKY_IMAGE_COVERAGE_CUTOFF = 0.85;

/** Minimum gap between image and sticky footer on mobile (px). */
export const FULLSCREEN_STICKY_MIN_SAFE_GAP_PX = 12;

export type FullscreenStickyAdVisibilityInput = {
    readonly isFullscreenOpen: boolean;
    readonly viewportWidth: number;
    readonly viewportHeight: number;
    readonly imageAspectRatio: number | null;
    readonly safeAreaBottomInsetPx: number;
};

/**
 * Whether the fullscreen sticky-footer anchor should mount (desktop always; mobile uses letterbox/safe-area heuristics).
 */
export function computeShouldRenderFullscreenStickyAd(input: FullscreenStickyAdVisibilityInput): boolean {
    if (!input.isFullscreenOpen) {
        return false;
    }
    if (input.viewportWidth >= FULLSCREEN_STICKY_FOOTER_DESKTOP_MIN_WIDTH_PX) {
        return true;
    }
    if (!input.imageAspectRatio) {
        return true;
    }
    const viewportWidth = input.viewportWidth;
    const viewportHeight = input.viewportHeight;
    if (!viewportWidth || !viewportHeight) {
        return false;
    }
    const viewportAspectRatio = viewportWidth / viewportHeight;
    const displayedImageHeight =
        input.imageAspectRatio > viewportAspectRatio
            ? viewportWidth / input.imageAspectRatio
            : viewportHeight;
    const imageCoverageRatio = displayedImageHeight / viewportHeight;
    const letterboxHeight = Math.max(0, viewportHeight - displayedImageHeight);
    const bottomLetterboxHeight = letterboxHeight / 2;
    const bottomSafeSpace = Math.max(
        0,
        bottomLetterboxHeight - STICKY_FOOTER_AD_RESERVED_HEIGHT_PX - input.safeAreaBottomInsetPx,
    );
    if (imageCoverageRatio > FULLSCREEN_STICKY_IMAGE_COVERAGE_CUTOFF) {
        return false;
    }
    return bottomSafeSpace >= FULLSCREEN_STICKY_MIN_SAFE_GAP_PX;
}

/**
 * Reads resolved `env(safe-area-inset-bottom)` in CSS pixels.
 */
export function getFullscreenSafeAreaInsetBottomPx(): number {
    if (typeof document === "undefined") {
        return 0;
    }
    const probe = document.createElement("div");
    probe.style.position = "fixed";
    probe.style.bottom = "0";
    probe.style.paddingBottom = "env(safe-area-inset-bottom)";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    document.body.appendChild(probe);
    const insetValue = window.getComputedStyle(probe).paddingBottom;
    probe.remove();
    const parsedValue = Number.parseFloat(insetValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

/**
 * Removes the Fancybox-injected sticky-footer anchor (PropertyInfo / modal fullscreen path).
 */
/**
 * Best-effort URL for the active Fancybox slide image (gallery anchors use `data-src`).
 */
export function resolveFancyboxActiveSlideImageUrl(fancybox: { getSlide?: () => any }): string | null {
    const slide = fancybox?.getSlide?.();
    if (!slide) {
        return null;
    }
    const trigger = slide.triggerEl as HTMLElement | undefined;
    const fromData = trigger?.dataset?.src ?? trigger?.getAttribute?.("data-src");
    if (fromData) {
        return fromData;
    }
    const img = slide.contentEl?.querySelector?.("img") as HTMLImageElement | null | undefined;
    return img?.currentSrc || img?.src || null;
}

export function removeFullscreenFancyboxFooterAd(): void {
    if (typeof document === "undefined") {
        return;
    }
    document.getElementById(FULLSCREEN_FANCYBOX_FOOTER_AD_ELEMENT_ID)?.remove();
}

/**
 * Appends the sticky-footer anchor to the Fancybox container when inventory should show. Safe-area padding is measured at call time so first open is correct.
 */
export function attachFullscreenFancyboxFooterAd(fancyboxInstance?: { container?: HTMLElement } | null): void {
    if (typeof document === "undefined") {
        return;
    }
    const existing = document.getElementById(FULLSCREEN_FANCYBOX_FOOTER_AD_ELEMENT_ID);
    if (existing) {
        return;
    }
    const container =
        fancyboxInstance?.container ??
        document.querySelector(".fancybox__container") ??
        document.body;
    if (!container) {
        return;
    }
    const footer = document.createElement("div");
    footer.id = FULLSCREEN_FANCYBOX_FOOTER_AD_ELEMENT_ID;
    footer.className = "priceme-fullscreen-sticky-footer-ad";
    footer.style.position = "fixed";
    footer.style.left = "0";
    footer.style.right = "0";
    footer.style.bottom = "0";
    footer.style.zIndex = "2147483647";
    footer.style.pointerEvents = "none";
    const paddingBottomPx =
        Math.max(0, getFullscreenSafeAreaInsetBottomPx()) + FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX;
    footer.innerHTML = `
      <div style="max-width:728px;margin:0 auto;padding:0 12px;pointer-events:auto;padding-bottom:${paddingBottomPx}px">
        <div style="width:100%;height:${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px" ${PRICEME_AD_PLACEMENT_ATTR}="" ${PRICEME_AD_TYPE_ATTR}="sticky-footer"></div>
      </div>
    `;
    container.appendChild(footer);
}
